// Bootstrap 5 grid: column classes per breakpoint, mobile first.
// A breakpoint without its own value inherits the value of the next smaller one.
import { classList, replaceClasses } from './dom.js';

export const BREAKPOINTS = [
    { key: '', label: 'XS', min: 0 },
    { key: 'sm', label: 'SM', min: 576 },
    { key: 'md', label: 'MD', min: 768 },
    { key: 'lg', label: 'LG', min: 992 },
    { key: 'xl', label: 'XL', min: 1200 },
    { key: 'xxl', label: 'XXL', min: 1400 },
];

export const BREAKPOINT_KEYS = BREAKPOINTS.map(bp => bp.key);

const COLUMN_CLASS = /^col(?:-(sm|md|lg|xl|xxl))?(?:-(auto|[1-9]|1[0-2]))?$/;

export function isColumnClass(name) {
    return COLUMN_CLASS.test(name);
}

export function isColumn(el) {
    return !!el && el.nodeType === 1 && classList(el).some(isColumnClass);
}

/**
 * Reads the column classes of an element: {'': '12', md: '6', lg: 'auto', xl: 'fill'}.
 * 'fill' stands for the bare "col" / "col-md" classes (equal share of the row).
 */
export function readColumn(el) {
    const spec = {};
    for (const name of classList(el)) {
        const match = name.match(COLUMN_CLASS);
        if (!match) continue;
        spec[match[1] || ''] = match[2] || 'fill';
    }
    return spec;
}

export function columnClass(breakpoint, value) {
    const prefix = breakpoint ? `col-${breakpoint}` : 'col';
    return value === 'fill' ? prefix : `${prefix}-${value}`;
}

export function writeColumn(el, spec) {
    const next = BREAKPOINT_KEYS
        .filter(bp => spec[bp] !== undefined && spec[bp] !== '' && spec[bp] !== null)
        .map(bp => columnClass(bp, String(spec[bp])));
    // A column always keeps one class, or it would lose its column behaviour.
    replaceClasses(el, COLUMN_CLASS, next.length ? next : ['col']);
}

/** The value a breakpoint really uses, and the breakpoint it comes from. */
export function effectiveColumn(spec, breakpoint) {
    const index = BREAKPOINT_KEYS.indexOf(breakpoint);
    for (let i = index; i >= 0; i--) {
        const key = BREAKPOINT_KEYS[i];
        if (spec[key] !== undefined) return { value: spec[key], from: key };
    }
    return { value: '12', from: null }; // a column without a class for this size spans the whole row
}

/** The Bootstrap breakpoint active for a canvas width. */
export function breakpointForWidth(width) {
    let current = '';
    for (const bp of BREAKPOINTS) if (width >= bp.min) current = bp.key;
    return current;
}

/** Numeric width (1–12) of a column at a breakpoint, or null for auto / fill. */
export function columnSpan(el, breakpoint) {
    const { value } = effectiveColumn(readColumn(el), breakpoint);
    return /^\d+$/.test(value) ? parseInt(value, 10) : null;
}

/** Sets the width of a column for one breakpoint, keeping the others. */
export function setColumnSpan(el, breakpoint, value) {
    const spec = readColumn(el);
    if (value === '' || value === null || value === undefined) delete spec[breakpoint];
    else spec[breakpoint] = String(value);
    writeColumn(el, spec);
}

/**
 * The breakpoint a direct change applies from, for a canvas breakpoint: phones (xs, sm) edit the base,
 * tablets edit md, desktops edit lg. A change seen on a desktop is then the same on every desktop.
 */
export function editingBreakpoint(breakpoint) {
    if (breakpoint === '' || breakpoint === 'sm') return '';
    return breakpoint === 'md' ? 'md' : 'lg';
}

/** Sets a width from a breakpoint up: larger breakpoints lose their own value so the change shows. */
export function setColumnSpanFrom(el, breakpoint, value) {
    const spec = readColumn(el);
    const index = BREAKPOINT_KEYS.indexOf(breakpoint);
    BREAKPOINT_KEYS.slice(index + 1).forEach(key => delete spec[key]);
    spec[breakpoint] = String(value);
    writeColumn(el, spec);
}

/**
 * Columns of a new row: stacked on phones, side by side from `breakpoint`.
 * sizes = [6, 6] gives "col-12 col-md-6" twice.
 */
export function newColumnClasses(size, breakpoint = 'md') {
    return size === 12 ? 'col-12' : `col-12 ${columnClass(breakpoint, String(size))}`;
}

/**
 * After a column is added or removed, spreads the 12 units again when every column
 * has a numeric width at the same breakpoint (custom layouts are left alone).
 */
export function rebalanceRow(row, breakpoint = null) {
    const columns = Array.from(row.children).filter(child => child.getAttribute('data-pf') === 'col');
    if (!columns.length) return false;
    const specs = columns.map(readColumn);
    const candidates = breakpoint !== null ? [breakpoint] : ['md', 'lg', 'sm', 'xl', 'xxl', ''];
    for (const bp of candidates) {
        if (!specs.every(spec => /^\d+$/.test(spec[bp] || ''))) continue;
        const total = specs.reduce((sum, spec) => sum + parseInt(spec[bp], 10), 0);
        if (total === 12 || columns.length > 12) return false;
        const base = Math.floor(12 / columns.length);
        const extra = 12 % columns.length;
        columns.forEach((col, index) => setColumnSpan(col, bp, base + (index < extra ? 1 : 0)));
        return true;
    }
    return false;
}

/** Equal widths for every column of a row at a breakpoint. */
export function equalizeRow(row, breakpoint) {
    const columns = Array.from(row.children).filter(child => child.getAttribute('data-pf') === 'col');
    if (!columns.length || columns.length > 12) return;
    const base = Math.floor(12 / columns.length);
    const extra = 12 % columns.length;
    columns.forEach((col, index) => setColumnSpan(col, breakpoint, base + (index < extra ? 1 : 0)));
}
